import { Input } from "antd";
import MainLayout from "../../layout/MainLayout";
import uploadIcon from "../../assets/icons/upload-icon.png";
import {
  ToggleButton,
  Button1,
  Select,
  Radio,
  TagInput,
  QuickActionSelect,
} from "../index";
import searchIcon from "../../assets/icons/search-icon.png";
import { useState, useMemo, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { beneficiariesApi } from "../../api/endpoints";
import { useBeneficiaries, usePayoutProviders } from "../../data/live";

const CURRENCIES = ["AUD", "USD", "EUR", "GBP", "PKR", "CNY", "CAD", "INR"];
const COUNTRIES = [
  { name: "Australia", value: "AU" },
  { name: "United States", value: "US" },
  { name: "United Kingdom", value: "GB" },
  { name: "Pakistan", value: "PK" },
  { name: "India", value: "IN" },
  { name: "Canada", value: "CA" },
];
const PURPOSES = [
  "Family Support",
  "Salary",
  "Bill Payment",
  "Education",
  "Medical",
  "Business",
  "Other",
];
const CATEGORIES = ["Family", "Friend", "Vendor", "Employee", "Business"];
const BANK_LOCATIONS = COUNTRIES.map((c) => ({ name: c.name, value: c.value }));

const AddRecipient = () => {
  const navigate = useNavigate();
  const location = useLocation();
  // Saved recipients, so the search box above the form can actually find one.
  // It was previously an Input with no value, no handler and no data behind
  // it, typing in it did nothing at all.
  const { beneficiaries } = useBeneficiaries();
  const [recipientSearch, setRecipientSearch] = useState("");
  const editingRecipient = location.state?.recipient || null;
  const editingRecipientId = editingRecipient?.id;
  const editingBankAccount = editingRecipient?.bankAccounts?.[0] || {};
  const paymentTypeFromApi = {
    LOCAL: "Local Payment",
    BILLS: "Bills Payment",
    INTERNATIONAL: "International Payment",
  };
  const editingTransferMethod = editingRecipient?.transferMethod || "";

  const [paymentType, setPaymentType] = useState(
    paymentTypeFromApi[editingRecipient?.paymentType] || "Bills Payment"
  );
  const [accountType, setAccountType] = useState(
    editingRecipient?.accountType || "INDIVIDUAL"
  );
  // The chosen payout partner, held by code rather than list position, the
  // list now comes from the API and changes with the currency.
  const [transferMethod, setTransferMethod] = useState(editingTransferMethod);
  const [form, setForm] = useState({
    fullName: editingRecipient?.fullName || "",
    businessName: editingRecipient?.businessName || "",
    nickname: editingRecipient?.nickname || "",
    email: editingRecipient?.email || "",
    mobile: editingRecipient?.mobile || "",
    countryCode: editingRecipient?.countryCode || "AU",
    address: editingRecipient?.address || "",
    city: editingRecipient?.city || "",
    state: editingRecipient?.state || "",
    zipcode: editingRecipient?.zipcode || "",
    bankLocation: editingRecipient?.countryCode || "AU",
    currency: editingBankAccount.currency || "AUD",
    bankName: editingBankAccount.bankName || "",
    iban: editingBankAccount.iban || editingBankAccount.accountNumber || "",
    swiftCode: editingBankAccount.swiftCode || "",
    sortCode: editingBankAccount.sortCode || editingBankAccount.routingNumber || "",
    purpose: editingRecipient?.purpose || "",
    category: editingRecipient?.category || "",
    tags: editingRecipient?.tags || [],
  });
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);

  const updateField = (field, value) =>
    setForm((prev) => ({ ...prev, [field]: value }));

  const onPaymentTypeChange = (val) => setPaymentType(val);

  const handleAccountTypeChange = (type) => setAccountType(type);


  const validate = () => {
    if (accountType === "INDIVIDUAL" && !form.fullName.trim()) {
      return "Full name is required";
    }
    if (accountType === "BUSINESS" && !form.businessName.trim()) {
      return "Business name is required";
    }
    return null;
  };

  // Who can actually pay out in the destination currency.
  const { providers: payoutProviders, loading: providersLoading } =
    usePayoutProviders(form.currency);
  const selectableProviders = payoutProviders.filter((p) => p.status === "LIVE");

  useEffect(() => {
    // A currency change can invalidate the chosen partner. Default to the one
    // live option when there is exactly one, and clear it otherwise rather
    // than leaving a partner selected that cannot serve this corridor.
    if (selectableProviders.some((p) => p.code === transferMethod)) return;
    setTransferMethod(selectableProviders.length === 1 ? selectableProviders[0].code : "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.currency, payoutProviders.length]);

  const recipientMatches = useMemo(() => {
    const q = recipientSearch.trim().toLowerCase();
    if (q.length < 2) return [];
    return beneficiaries
      .filter((b) =>
        [b.fullName, b.businessName, b.nickname, b.email, b.mobile]
          .filter(Boolean)
          .join(" ")
          .toLowerCase()
          .includes(q)
      )
      .slice(0, 6);
  }, [beneficiaries, recipientSearch]);

  /** Picking a saved recipient fills the form from their existing details. */
  const applyRecipient = (b) => {
    const bank = b.bankAccounts?.[0] || {};
    setForm((f) => ({
      ...f,
      fullName: b.fullName || "",
      businessName: b.businessName || "",
      nickname: b.nickname || "",
      email: b.email || "",
      mobile: b.mobile || "",
      countryCode: b.countryCode || f.countryCode,
      address: b.address || "",
      city: b.city || "",
      state: b.state || "",
      zipcode: b.zipcode || "",
      purpose: b.purpose || f.purpose,
      category: b.category || f.category,
      bankName: bank.bankName || "",
      iban: bank.iban || bank.accountNumber || "",
      swiftCode: bank.swiftCode || "",
      sortCode: bank.sortCode || bank.routingNumber || "",
      currency: bank.currency || f.currency,
    }));
    setRecipientSearch("");
  };

  const handleSaveBeneficiary = async () => {
    const err = validate();
    if (err) {
      setSaveError(err);
      return;
    }

    setSaving(true);
    setSaveError(null);


  const paymentTypeMap = {
      "Local Payment": "LOCAL",
      "Bills Payment": "BILLS",
      "International Payment": "INTERNATIONAL",
    };

    try {
      const payload = {
        accountType,
        fullName: accountType === "INDIVIDUAL" ? form.fullName : undefined,
        businessName: accountType === "BUSINESS" ? form.businessName : undefined,
        nickname: form.nickname || undefined,
        email: form.email || undefined,
        mobile: form.mobile || undefined,
        countryCode: form.countryCode,
        address: form.address || undefined,
        city: form.city || undefined,
        state: form.state || undefined,
        zipcode: form.zipcode || undefined,
        purpose: form.purpose || undefined,
        category: form.category || undefined,
        transferMethod,
        paymentType: paymentTypeMap[paymentType],
        payoutMethod: "BANK_ACCOUNT",
        bankAccount: {
          bankName: form.bankName || undefined,
          iban: form.iban || undefined,
          accountNumber: form.iban || undefined,
          swiftCode: form.swiftCode || undefined,
          sortCode: form.sortCode || undefined,
          routingNumber: form.sortCode || undefined,
          currency: form.currency,
        },
      };

      if (editingRecipientId) {
        await beneficiariesApi.update(editingRecipientId, payload);
      } else {
        await beneficiariesApi.add(payload);
      }
      navigate("/recipient");
    } catch (e) {
      const detail =
        e.response?.data?.details?.[0]?.message ||
        e.response?.data?.error ||
        e.message ||
        "Save failed";
      setSaveError(detail);
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <MainLayout
        expand={false}
        NavName="Recipients"
        pageClassName="dashboard-page add-recipient-page"
        contentClassName="dashboard-content"
      >
        <div className="add-recipient-shell">
          <div className="page-action-header">
            <h2>{editingRecipientId ? "Edit Recipient" : "Add Recipients"}</h2>
            <QuickActionSelect />
          </div>

            <div className="add-recipient-card row mt-4 justify-content-between p-3 p-md-4 mx-1">
              <div className="add-recipient-tabs d-flex justify-content-center align-items-center">
                <ToggleButton
                  options={[
                    "Local Payment",
                    "Bills Payment",
                    "International Payment",
                  ]}
                  defaultValue={paymentType}
                  onChange={onPaymentTypeChange}
                />
              </div>

              <div className="col-lg-6 mb-4 p-2 p-md-0 pt-md-2">
                <div className="add-recipient-section h-100">
                <span className="add-recipient-title">Recipient's Account</span>
                <div className="d-flex flex-column mt-4 gap-2">
                  <div className="position-relative">
                    <Input
                      placeholder="Search saved recipients"
                      prefix={<img src={searchIcon} alt="search" width={18} />}
                      className="add-recipient-input p-2"
                      value={recipientSearch}
                      onChange={(e) => setRecipientSearch(e.target.value)}
                    />
                    {recipientMatches.length > 0 && (
                      <div
                        className="position-absolute w-100 bg-white border rounded-3 shadow-sm mt-1"
                        style={{ zIndex: 20, maxHeight: 220, overflowY: "auto" }}
                      >
                        {recipientMatches.map((b) => (
                          <button
                            key={b.id}
                            type="button"
                            className="btn btn-link text-decoration-none text-start w-100 px-3 py-2 text-dark"
                            onClick={() => applyRecipient(b)}
                          >
                            <span className="fw-semibold d-block">
                              {b.fullName || b.businessName}
                            </span>
                            <span className="fs12px text-muted">
                              {[b.nickname, b.email, b.bankAccounts?.[0]?.currency]
                                .filter(Boolean)
                                .join(" · ") || "Saved recipient"}
                            </span>
                          </button>
                        ))}
                      </div>
                    )}
                    {recipientSearch.trim().length >= 2 && recipientMatches.length === 0 && (
                      <div className="fs12px text-muted mt-1 px-1">
                        No saved recipient matches “{recipientSearch.trim()}”. Fill the form below to add a new one.
                      </div>
                    )}
                  </div>

                  <div className="mt-1">
                    <label className="fs12px mb-2">Email/Phone Number</label>
                    <Input
                      placeholder="Recipient's email/phone number"
                      className="add-recipient-input p-2"
                      value={form.mobile}
                      onChange={(e) => updateField("mobile", e.target.value)}
                    />
                  </div>

                  <div className="mt-1 d-flex flex-column gap-2">
                    <span className="fs12px">Recipients account type</span>
                    <div className="d-flex justify-content-between  ">
                      <div className="d-flex gap-2 align-items-center w-50 ">
                        <Radio
                          name={"account-type"}
                          accentColor={"green"}
                          height={"20px"}
                          width={"20px"}
                          checked={accountType === "BUSINESS"}
                          onClick={() => handleAccountTypeChange("BUSINESS")}
                        />
                        <span className="fs14px">Business</span>
                      </div>
                      <div className="d-flex gap-2 align-items-center w-50 justify-content-end justify-content-md-start">
                        <Radio
                          name={"account-type"}
                          accentColor={"green"}
                          height={"20px"}
                          width={"20px"}
                          checked={accountType === "INDIVIDUAL"}
                          onClick={() => handleAccountTypeChange("INDIVIDUAL")}
                        />
                        <span className="fs14px">Individual</span>
                      </div>
                    </div>
                  </div>

                  <div className="mt-1">
                    <label className="fs12px mb-2">Bank location</label>
                    <Select
                      defaultValue="Select bank location"
                      value={form.bankLocation}
                      options={BANK_LOCATIONS}
                      onChange={(e) =>
                        updateField("bankLocation", e.target.value)
                      }
                      className="add-recipient-select p-2"
                    />
                    <span className="fs12px" style={{ color: "#7D7D7D" }}>
                      The recipient’s bank location
                    </span>
                  </div>

                  <div className="mt-1">
                    <label className="fs12px mb-2">Account currency</label>
                    <Select
                      defaultValue="Select account currency"
                      value={form.currency}
                      options={CURRENCIES.map((c) => ({ name: c, value: c }))}
                      onChange={(e) => updateField("currency", e.target.value)}
                      className="add-recipient-select p-2"
                    />
                    <span className="fs12px" style={{ color: "#7D7D7D" }}>
                      The recipient’s bank account currency
                    </span>
                  </div>

                  <div className="d-flex flex-column gap-2 mt-1">
                    <span className="fs12px" style={{ color: "#7D7D7D" }}>
                      Transfer method
                    </span>
                    {providersLoading ? (
                      <span className="fs12px text-muted">Checking available partners…</span>
                    ) : selectableProviders.length === 0 ? (
                      <div className="fs12px text-muted border rounded-3 p-3">
                        ARM Financial cannot pay out in {form.currency} yet. You can still save
                        this recipient, but transfers to them are not available until a partner
                        is live for this currency.
                      </div>
                    ) : (
                      <div className="d-flex flex-wrap gap-2">
                        {selectableProviders.map((p) => (
                          <div
                            key={p.code}
                            className={`add-recipient-method d-flex justify-content-between rounded-3 transfer-method-card ${
                              p.code === transferMethod ? "selected-card" : "border"
                            }  p-3`}
                          >
                            <div className="d-flex flex-column">
                              <span className="fs14px fw-medium">{p.label}</span>
                              {p.note && (
                                <span className="fs12px text-muted">{p.note}</span>
                              )}
                            </div>
                            <Radio
                              height={"20px"}
                              width={"24px"}
                              accentColor={"green"}
                              name={"transfer-method"}
                              checked={p.code === transferMethod}
                              onClick={() => setTransferMethod(p.code)}
                            />
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  <div className="d-flex flex-column gap-3 mt-4">
                    <span className="fs-5 fw-medium">Additional Details</span>
                    <div className="d-flex flex-column gap-2">
                      <label className="fs12px">Avatar</label>
                      <Input
                        className="add-recipient-input p-2"
                        placeholder="Upload or drag files here"
                        suffix={
                          <img
                            src={uploadIcon}
                            alt="upload"
                            className="cursor-pointer"
                          />
                        }
                      />
                    </div>
                    <div className="d-flex flex-column gap-2">
                      <label className="fs12px">Category</label>
                      <Select
                        defaultValue="Choose Category"
                        value={form.category}
                        options={CATEGORIES.map((c) => ({ name: c, value: c }))}
                        onChange={(e) =>
                          updateField("category", e.target.value)
                        }
                        className="add-recipient-select p-2"
                      />
                    </div>
                    <div className="d-flex flex-column gap-2 ">
                      <label className="fs12px ">Tags</label>
                      <TagInput
                        className="add-recipient-tag-input border py-3"
                        inputBgColor="transparent"
                        tagColor="#1bbb6b"
                        tagTextColor="#000"
                        limit={10}
                        placeholder="Add up to 10 Tags"
                      />
                    </div>
                    <div className="d-flex flex-column gap-2">
                      <label className="fs12px">Purpose</label>
                      <Select
                        defaultValue="Choose Purpose"
                        value={form.purpose}
                        options={PURPOSES.map((p) => ({ name: p, value: p }))}
                        onChange={(e) =>
                          updateField("purpose", e.target.value)
                        }
                        className="add-recipient-select p-2"
                      />
                    </div>
                  </div>
                </div>
                </div>
              </div>

              <div className="col-lg-6 mb-4 p-2 p-md-2 ">
                <div className="add-recipient-section h-100">
                <span className="add-recipient-title">Recipient’s Information</span>
                <div className="d-flex flex-column mt-4 gap-3">
                  {accountType === "BUSINESS" ? (
                    <div className="d-flex flex-column gap-2">
                      <label className="fs12px">Business name</label>
                      <Input
                        className="add-recipient-input p-2"
                        placeholder="Business name"
                        value={form.businessName}
                        onChange={(e) =>
                          updateField("businessName", e.target.value)
                        }
                      />
                    </div>
                  ) : (
                    <div className="d-flex flex-column gap-2">
                      <label className="fs12px">Full name</label>
                      <Input
                        className="add-recipient-input p-2"
                        placeholder="e.g. Savannah Nguyen"
                        value={form.fullName}
                        onChange={(e) =>
                          updateField("fullName", e.target.value)
                        }
                      />
                    </div>
                  )}

                  <div className="d-flex flex-column gap-2">
                    <label className="fs12px">BSB/Routing/Sort Code</label>
                    <Input
                      className="add-recipient-input p-2"
                      placeholder="Fedwire routing number"
                      value={form.sortCode}
                      onChange={(e) => updateField("sortCode", e.target.value)}
                    />
                    <span className="fs12px" style={{ color: "#7D7D7D" }}>
                      9-digits number to identify a bank in the US
                    </span>
                  </div>

                  <div className="d-flex flex-column gap-2">
                    <label className="fs12px">SWIFT/BIC code</label>
                    <Input
                      className="add-recipient-input p-2"
                      placeholder="SWIFT/BIC code"
                      value={form.swiftCode}
                      onChange={(e) => updateField("swiftCode", e.target.value)}
                    />
                  </div>

                  <div className="d-flex flex-column gap-2">
                    <label className="fs12px">Bank name</label>
                    <Input
                      className="add-recipient-input p-2"
                      placeholder="Bank name"
                      value={form.bankName}
                      onChange={(e) => updateField("bankName", e.target.value)}
                    />
                  </div>

                  <div className="d-flex flex-column gap-2">
                    <label className="fs12px">IBAN/Account Number</label>
                    <Input
                      className="add-recipient-input p-2"
                      placeholder="Account number"
                      value={form.iban}
                      onChange={(e) => updateField("iban", e.target.value)}
                    />
                  </div>

                  <div className="d-flex flex-column gap-2">
                    <label className="fs12px">Country or region</label>
                    <Select
                      className="add-recipient-select p-2"
                      defaultValue="Select country"
                      value={form.countryCode}
                      options={COUNTRIES}
                      onChange={(e) =>
                        updateField("countryCode", e.target.value)
                      }
                    />
                  </div>

                  <div className="d-flex flex-column gap-2">
                    <label className="fs12px">Address</label>
                    <Input
                      className="add-recipient-input p-2"
                      placeholder="Your address"
                      value={form.address}
                      onChange={(e) => updateField("address", e.target.value)}
                    />
                    <span className="fs12px" style={{ color: "#7D7D7D" }}>
                      Recipient's address (should not be a PO or GPO box
                      address)
                    </span>
                  </div>

                  <div className="d-flex flex-column gap-2">
                    <label className="fs12px">City</label>
                    <Input
                      className="add-recipient-input p-2"
                      placeholder="City"
                      value={form.city}
                      onChange={(e) => updateField("city", e.target.value)}
                    />
                  </div>

                  <div className="d-flex flex-column gap-2">
                    <label className="fs12px">State</label>
                    <Input
                      className="add-recipient-input p-2"
                      placeholder="State"
                      value={form.state}
                      onChange={(e) => updateField("state", e.target.value)}
                    />
                  </div>

                  <div className="d-flex flex-column gap-2">
                    <label className="fs12px">Zipcode</label>
                    <Input
                      className="add-recipient-input p-2"
                      placeholder="Zipcode"
                      value={form.zipcode}
                      onChange={(e) => updateField("zipcode", e.target.value)}
                    />
                  </div>

                  <div className="d-flex flex-column gap-2">
                    <label className="fs12px">
                      Nickname{" "}
                      <span className="fs12px" style={{ color: "#7D7D7D" }}>
                        (Optional)
                      </span>
                    </label>
                    <Input
                      className="add-recipient-input p-2"
                      placeholder="Your nickname"
                      value={form.nickname}
                      onChange={(e) => updateField("nickname", e.target.value)}
                    />
                    <span className="fs12px" style={{ color: "#7D7D7D" }}>
                      For your reference only. Nickname are only visible to you
                      and will not be sent to the recipient
                    </span>
                  </div>

                  <div className="d-flex flex-column gap-2">
                    <label className="fs12px">
                      Email{" "}
                      <span className="fs12px" style={{ color: "#7D7D7D" }}>
                        (Optional)
                      </span>
                    </label>
                    <Input
                      className="add-recipient-input p-2"
                      placeholder="example@gmail.com"
                      value={form.email}
                      onChange={(e) => updateField("email", e.target.value)}
                    />
                  </div>
                </div>
                </div>
              </div>

              {saveError && (
                <div className="col-12 p-2">
                  <div className="fs12px" style={{ color: "#FC555B" }}>
                    {saveError}
                  </div>
                </div>
              )}

              <div className="add-recipient-actions d-flex justify-content-md-end justify-content-between p-2 gap-3 pt-3">
                <Button1
                  buttonName={"Save as Draft"}
                  className={"btn-outline-success"}
                />
                <Button1
                  buttonName={saving ? "Saving..." : editingRecipientId ? "Update Recipient" : "Add Recipient"}
                  className="btn-dark "
                  onClick={handleSaveBeneficiary}
                  disabled={saving}
                />
              </div>
            </div>
          </div>
      </MainLayout>
    </>
  );
};

export default AddRecipient;
